import { useEffect, useState } from 'react';
import { REFUSAL_REASON_ID_PREFIX, refusalReasonId } from '@/utils/gateControl';

/**
 * F-0324 AC-DH34 (`docs/concept-access-control.md` §19): the description every refused control names.
 *
 * `gateControlProps` and `gateFieldProps` give a refused control `aria-describedby` an id derived
 * from its reason (`refusalReasonId`) and its reason as `title`. This draws one node per id a control
 * in the document names, holding that reason, so a screen reader reads the reason as the control's
 * description — a title alone may or may not be read.
 *
 * - **One node per reason, counted by its controls.** The set is read from the document itself: a
 *   node is drawn while at least one control names it and goes with the last one, whichever way that
 *   control left (an unmount, a gate that opened, a page change).
 * - **Nothing is written while React renders.** The controls are read by a `MutationObserver`, whose
 *   callback runs after the commit that changed them; `gateControlProps` stays a plain function.
 * - **In the accessibility tree.** The nodes are visually hidden (`sr-only`), never `display: none`
 *   or `hidden`, so every engine takes them as a description source.
 *
 * A control whose `title` does not hash to the id it names (a later spread replaced the title) gets
 * no node rather than a wrong one. Mounted once, beside `RefusedTouchHint`, in `App.tsx`.
 */
export function RefusalReasons() {
  const [reasons, setReasons] = useState<ReadonlyMap<string, string>>(() => new Map());

  useEffect(() => {
    const read = () => {
      const found = namedRefusalReasons(document);
      setReasons((current) => (sameReasons(current, found) ? current : found));
    };
    read();
    const observer = new MutationObserver(read);
    observer.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['aria-describedby', 'title'],
    });
    return () => observer.disconnect();
  }, []);

  return (
    <div data-refusal-reasons="">
      {[...reasons].map(([id, reason]) => (
        <span key={id} id={id} className="sr-only">
          {reason}
        </span>
      ))}
    </div>
  );
}

/** Every refusal-reason id a control in `root` names, with the reason its `title` carries. */
export function namedRefusalReasons(root: ParentNode): Map<string, string> {
  const found = new Map<string, string>();
  for (const control of root.querySelectorAll(
    `[aria-describedby*="${REFUSAL_REASON_ID_PREFIX}"]`,
  )) {
    const reason = control.getAttribute('title')?.trim();
    if (!reason) continue;
    for (const id of (control.getAttribute('aria-describedby') ?? '').split(/\s+/)) {
      if (id.startsWith(REFUSAL_REASON_ID_PREFIX) && refusalReasonId(reason) === id) {
        found.set(id, reason);
      }
    }
  }
  return found;
}

function sameReasons(a: ReadonlyMap<string, string>, b: ReadonlyMap<string, string>): boolean {
  if (a.size !== b.size) return false;
  for (const [id, reason] of a) if (b.get(id) !== reason) return false;
  return true;
}
